<template>
    <Head :title="t('teamsSignIn.consent.headTitle')" />
    <div class="flex flex-col min-h-screen overflow-y-auto h-screen bg-surface text-ink">
        <div class="fade-in grow justify-center gap-5 md:gap-10 flex flex-col items-center py-4 md:py-10">
            <div class="flex flex-col gap-4 justify-around text-center max-w-sm mx-auto px-4">
                <div class="flex flex-col pb-5 justify-center">
                    <img :src="Logo" :alt="t('teamsSignIn.logoAlt')" class="mx-auto h-16 w-auto" />
                </div>
            </div>
            <template v-if="!termsConsented">
                <div class="flex flex-col gap-4 items-center max-w-md">
                    <div class="text-center mb-4">
                        <h2 class="title-body mb-2">{{ copy.heading }}</h2>
                        <p class="body-small text-ink-muted">{{ copy.body }}</p>
                    </div>

                    <div class="max-w-md mx-auto w-full">
                        <TeamsTerms v-model:terms="terms" :terms-content="org_terms_content" :modal="modal_terms" @accept="acceptTerms" />
                    </div>

                    <BaseNotification v-if="submitFailed" theme="error" hide-dismiss :text="t('teamsSignIn.consent.saveFailed')" />

                    <LoadingSpinner v-if="loading" class="w-10 h-10" />
                    <BaseButton v-else-if="!modal_terms" type="button" theme="primary" :disabled="!terms" class="w-full" @click="acceptTerms">
                        {{ t("teamsSignIn.consent.continueButton") }}
                    </BaseButton>
                </div>
            </template>
            <template v-else>
                <LoadingSpinner v-if="loading" class="w-10 h-10" />
                <div v-else class="rounded-lg max-w-md py-4 text-center">
                    <p>{{ t("teamsSignIn.loggedIn.title") }}</p>
                    <p>{{ t("teamsSignIn.loggedIn.body") }}</p>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { Head } from "@inertiajs/vue3";
import Logo from "~img/color-logo.svg";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import TeamsTerms from "~vue/components/TermsConsent.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { submitFormNavigation } from "~vue/utils/formPost";
import { logError } from "~vue/utils/logUtils";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { token, terms_document_id, reconsent_reason } = defineProps({
    token: { type: String, default: null },
    org_terms_content: { type: String, default: null },
    terms_document_id: { type: String, required: true },
    modal_terms: { type: Boolean, default: false },
    reconsent_reason: { type: String, default: "first_time" },
});

// The heading carries the re-consent framing here, so the shared TermsConsent component
// is deliberately not passed the reason (its inline notice would double the message).
const RECONSENT_COPY = {
    first_time: () => ({ heading: t("teamsSignIn.consent.reconsent.firstTime.heading"), body: t("teamsSignIn.consent.reconsent.firstTime.body") }),
    updated: () => ({ heading: t("teamsSignIn.consent.reconsent.updated.heading"), body: t("teamsSignIn.consent.reconsent.updated.body") }),
    grandfathered: () => ({
        heading: t("teamsSignIn.consent.reconsent.grandfathered.heading"),
        body: t("teamsSignIn.consent.reconsent.grandfathered.body"),
    }),
};

const copy = computed(() => (RECONSENT_COPY[reconsent_reason] ?? RECONSENT_COPY.first_time)());

const loading = ref(false);
const terms = ref(false);
const termsConsented = ref(false);
const submitFailed = ref(false);

async function acceptTerms() {
    if (!terms.value) {
        return;
    }

    submitFailed.value = false;
    loading.value = true;

    try {
        const response = await fetch("/microsoft-teams/terms-of-consent/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                consent: true,
                terms_document_id,
                oauth_redirect: true,
                token,
            }),
        });

        if (response.ok) {
            const { oauth_form_post, redirect } = await response.json();
            // Re-entering the OAuth flow POSTs the token so a ~2.2 KB Entra JWT can't be
            // truncated by a URL-length cap (BRA-895). `redirect` still carries the
            // cross-server hand-off, which is short and stays a plain navigation.
            if (oauth_form_post) {
                submitFormNavigation(oauth_form_post.action, oauth_form_post.fields);
                return;
            }
            if (redirect) {
                globalThis.location.href = redirect;
            }
        } else {
            submitFailed.value = true;
            logError("Failed to submit terms consent", { sendToSlack: true });
        }
    } catch (error) {
        submitFailed.value = true;
        logError(error, { sendToSlack: true });
    } finally {
        loading.value = false;
    }
}
</script>
